import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';

export type ViewType = 'assistant-hub' | 'chat';
export type SidebarSection = 'bots' | 'workspaces' | 'favourites';

interface NavigationState {
  activeSidebarSection: SidebarSection;
  isSidebarExpanded: boolean;
  isTransitioning: boolean;
}

interface NavigationActions {
  setActiveSidebarSection: (section: SidebarSection) => void;
  toggleSidebar: () => void;
  setSidebarExpanded: (expanded: boolean) => void;
  setTransitioning: (isTransitioning: boolean) => void;
}

type NavigationStore = NavigationState & NavigationActions;

const initialState: NavigationState = {
  activeSidebarSection: 'bots',
  isSidebarExpanded: true,
  isTransitioning: false,
};

export const useNavigationStore = create<NavigationStore>()(
  devtools(
    persist(
      set => ({
        ...initialState,

        setActiveSidebarSection: section =>
          set(
            { activeSidebarSection: section },
            false,
            'setActiveSidebarSection'
          ),

        toggleSidebar: () =>
          set(
            state => ({
              isSidebarExpanded: !state.isSidebarExpanded,
            }),
            false,
            'toggleSidebar'
          ),

        setSidebarExpanded: expanded =>
          set({ isSidebarExpanded: expanded }, false, 'setSidebarExpanded'),

        setTransitioning: isTransitioning =>
          set({ isTransitioning }, false, 'setTransitioning'),
      }),
      {
        name: 'bot-unify-nav',
        version: 1,
        partialize: (state: NavigationStore) => ({
          activeSidebarSection: state.activeSidebarSection,
          isSidebarExpanded: state.isSidebarExpanded,
          // Exclude transient state from persistence
        }),
        migrate: (persistedState: unknown, _version: number) => {
          // Clean migration - removed old fields
          const state = persistedState as Record<string, unknown>;
          return {
            activeSidebarSection:
              (state.activeSidebarSection as SidebarSection) || 'bots',
            isSidebarExpanded: (state.isSidebarExpanded as boolean) ?? true,
            isTransitioning: false,
          } as NavigationState;
        },
      }
    ),
    {
      name: 'NavigationStore',
    }
  )
);

// Selectors with memoization
export const useActiveSidebarSection = () =>
  useNavigationStore(state => state.activeSidebarSection);
export const useIsSidebarExpanded = () =>
  useNavigationStore(state => state.isSidebarExpanded);
export const useIsTransitioning = () =>
  useNavigationStore(state => state.isTransitioning);

// Composite selectors with shallow comparison
export const useNavigationState = () =>
  useNavigationStore(
    state => ({
      activeSidebarSection: state.activeSidebarSection,
      isSidebarExpanded: state.isSidebarExpanded,
      isTransitioning: state.isTransitioning,
    }),
    shallow
  );

// Action selectors for performance
export const useNavigationActions = () =>
  useNavigationStore(
    state => ({
      setActiveSidebarSection: state.setActiveSidebarSection,
      toggleSidebar: state.toggleSidebar,
      setSidebarExpanded: state.setSidebarExpanded,
      setTransitioning: state.setTransitioning,
    }),
    shallow
  );
